import { Primary, SelectField } from '@cinch-labs/design-system'
import { dashify, formatNumber, formatPrice } from '@cinch-labs/shared-util'
import React, { useEffect, useRef, useState } from 'react'
import { getVehiclesDataByCondition } from '../../../data/get-vehicles-data-by-condition'

import {
  Makes,
  Models,
  VehicleCondition,
  VehiclesData,
} from '../../../types/vehicles-data'
import { Price } from '../filters/price/price'
import styles from './search-cars-form.module.css'
import {
  EventNames,
  TrackingEventTypes,
  useAnalytics,
} from '@cinch-nx/shared-analytics'
import { datadogRum } from '@datadog/browser-rum'
import { Datadog } from '@cinch-nx/landing-ui-shared'
const { Actions, Context } = Datadog

export interface SearchCarsFormProps {
  vehicleCondition: VehicleCondition
  vehicleData: VehiclesData
  section?: string
}

interface FieldOption {
  value: string
  label: string
}

enum PriceFrequency {
  MonthlyPrice = 'monthly-price',
  TotalPrice = 'total-price',
}

interface PriceFrequencyOption {
  value: PriceFrequency
  label: string
}

const PRICE_FREQUENCY_OPTIONS: Readonly<PriceFrequencyOption[]> = [
  {
    value: PriceFrequency.MonthlyPrice,
    label: 'Monthly price',
  },
  {
    value: PriceFrequency.TotalPrice,
    label: 'Total price',
  },
]

const getTotalPriceVehicles = async (vehicleCondition: VehicleCondition) => {
  const { vehiclesData } = await getVehiclesDataByCondition(
    vehicleCondition,
    false,
  )
  return vehiclesData
}

export const SearchCarsForm: React.FC<SearchCarsFormProps> = ({
  vehicleCondition,
  vehicleData,
}) => {
  const { totalStock, priceBucket, monthlyPriceBucket, makes } = vehicleData

  const monthlyPriceVehicles = vehicleData
  const totalPriceVehicles = useRef<VehiclesData>()
  const getVehiclesDataByConditionAndPrice = async (
    vehicleCondition: VehicleCondition,
    priceFrequency: PriceFrequency,
  ) => {
    if (priceFrequency === PriceFrequency.MonthlyPrice) {
      return monthlyPriceVehicles
    } else {
      if (!totalPriceVehicles.current) {
        totalPriceVehicles.current = await getTotalPriceVehicles(
          vehicleCondition,
        )
      }
      return totalPriceVehicles.current
    }
  }

  const [makeOptions, setMakeOptions] = useState<FieldOption[]>([
    { value: 'any', label: 'Any' },
    ...makes.map((make) => ({
      value: make.name,
      label: `${make.name} (${make.stock})`,
    })),
  ])
  const [selectedMakeOption, setSelectedMakeOption] = useState<string>()

  const [modelOptions, setModelOptions] = useState<FieldOption[]>([])
  const [selectedModelOption, setSelectedModelOption] = useState<string>()

  const [selectedMaxTotalPrice, setSelectedMaxTotalPrice] = useState<number>(-1)
  const [selectedPriceFrequencyOption, setSelectedPriceFrequencyOption] =
    useState<PriceFrequency>(PRICE_FREQUENCY_OPTIONS[0].value)

  const [filteredMakes, setFilteredMakes] = useState<Makes[]>(makes)

  const [searchResultsCount, setSearchResultsCount] =
    useState<number>(totalStock)

  const { trackEvent } = useAnalytics()

  const trackDropdownOptionChange = (
    trackEventLabel: string,
    trackEventAction: string,
    value: string,
    datadogActionName: string,
  ) => {
    trackEvent({
      eventName: `Homepage ${trackEventLabel} dropdown`,
      type: TrackingEventTypes.ADOBE,
      data: {
        event: {
          name: EventNames.click,
          category: 'hero',
          action: trackEventAction,
          label: trackEventLabel,
          value: value?.toLowerCase(),
        },
      },
    })

    datadogRum.addAction(datadogActionName, {
      service: Context.HERO_SEARCH,
      squad: Context.SQUAD,
      page: window.location.pathname,
      value: value?.toLowerCase(),
    })
  }

  const updateMakeOptions = (selectedMake?: string) => {
    // reset the make options so that only the selected make label does not display stock
    setMakeOptions([
      { value: 'any', label: 'Any' },
      ...filteredMakes.map((make) =>
        selectedMake && selectedMake === make.name
          ? {
              value: make.name,
              label: make.name,
            }
          : {
              value: make.name,
              label: `${make.name} (${make.stock})`,
            },
      ),
    ])
  }

  const resetModelOptions = (models?: Models[]) => {
    setSelectedModelOption(undefined)

    if (!models) {
      setModelOptions([])
    } else {
      setModelOptions([
        { value: 'any', label: 'Any' },
        ...models.map((model) => ({
          value: model.name,
          label: `${model.name} (${model.stock})`,
        })),
      ])
    }
  }

  const updateModelOptions = (models: Models[], selectedModel?: string) => {
    // reset the model options so that only the selected model label does not display stock
    setModelOptions([
      { value: 'any', label: 'Any' },
      ...models.map((model) =>
        selectedModel && selectedModel === model.name
          ? {
              value: model.name,
              label: model.name,
            }
          : {
              value: model.name,
              label: `${model.name} (${model.stock})`,
            },
      ),
    ])
  }

  const onMakeChange = (value: string) => {
    // use case for duplicate "any" values - if "any" = "any", then no change
    if (value === selectedMakeOption) return

    if (value === 'any') {
      setSearchResultsCount(totalStock)
      resetModelOptions()
    } else {
      const selectedMake = filteredMakes.find((x) => x.name === value)
      if (!selectedMake) return

      setSearchResultsCount(selectedMake.stock)

      if (selectedMake.models?.length) {
        resetModelOptions(selectedMake.models)
      }
    }

    setSelectedMakeOption(value)
    updateMakeOptions(value)
    trackDropdownOptionChange(
      'make',
      'select',
      value,
      Actions.SELECT_MAKE_OPTION,
    )
  }

  const onModelChange = (value: string) => {
    // use case for duplicate "any" values - if "any" = "any", then no change
    if (value === selectedModelOption) return

    const selectedMake = filteredMakes.find(
      (x) => x.name === selectedMakeOption,
    )

    if (!selectedMake) return //should always be a selected make

    if (value === 'any') {
      setSearchResultsCount(selectedMake.stock)
    } else {
      const selectedModel = selectedMake.models.find((x) => x.name === value)
      if (!selectedModel) return

      setSearchResultsCount(selectedModel.stock)
    }

    updateModelOptions(selectedMake.models, value)
    setSelectedModelOption(value)
    trackDropdownOptionChange(
      'model',
      'select',
      value,
      Actions.SELECT_MODEL_OPTION,
    )
  }

  useEffect(() => {
    updateMakeOptions(selectedMakeOption)

    const selectedMake = filteredMakes.find(
      (x) => x.name === selectedMakeOption,
    )

    if (!selectedMake) {
      getVehiclesDataByConditionAndPrice(
        vehicleCondition,
        selectedPriceFrequencyOption,
      )
        .then((vehicleData) => setSearchResultsCount(vehicleData.totalStock))
        .catch((error) => {
          console.error('Unable to get total stock:', error)
        })

      return
    }

    if (selectedMake?.models?.length) {
      resetModelOptions()

      if (selectedModelOption) {
        const selectedModel = selectedMake?.models.find(
          (x) => x.name === selectedModelOption,
        )
        if (selectedModel) {
          setSearchResultsCount(selectedModel.stock)
        }
      }
    }

    setSearchResultsCount(selectedMake?.stock)
  }, [filteredMakes])

  const onPriceFrequencyChange = (value: string) => {
    if (
      value !== PRICE_FREQUENCY_OPTIONS[0].value &&
      value !== PRICE_FREQUENCY_OPTIONS[1].value
    )
      return

    getVehiclesDataByConditionAndPrice(vehicleCondition, value)
      .then((vehicleData) => setFilteredMakes(vehicleData.makes))
      .catch((error) => console.error('Unable to get vehicle data:', error))

    setSelectedPriceFrequencyOption(value)

    if (value === PriceFrequency.MonthlyPrice) {
      trackDropdownOptionChange(
        'monthly price',
        'button',
        `${value} checked`,
        Actions.CLICK_MONTHLY_PRICE_RADIO,
      )
    } else {
      trackDropdownOptionChange(
        'total price',
        'button',
        `${value} checked`,
        Actions.CLICK_TOTAL_PRICE_RADIO,
      )
    }
  }

  const trackOnSubmit = () => {
    const make = selectedMakeOption ?? 'any'
    const model = selectedModelOption ?? 'any'

    // defaults for any price could be -1 or 0
    const formattedPrice =
      selectedMaxTotalPrice < 1 ? 'any' : formatPrice(selectedMaxTotalPrice)

    const priceSnippet =
      selectedPriceFrequencyOption === PriceFrequency.MonthlyPrice
        ? `monthly price: ${formattedPrice}`
        : `total price: ${formattedPrice}`

    const trackingValue = `search cars make: ${make}, model: ${model}, ${priceSnippet}`

    trackEvent({
      eventName: `Click on 'Search x ${vehicleCondition} cars' CTA button`,
      type: TrackingEventTypes.ADOBE,
      data: {
        event: {
          name: EventNames.click,
          category: 'hero',
          action: 'submit',
          label: 'search cars',
          value: trackingValue,
          type: vehicleCondition,
        },
      },
    })

    datadogRum.addAction(Actions.CLICK_SEARCH_CTA, {
      service: Context.HERO_SEARCH,
      squad: Context.SQUAD,
      page: window.location.pathname,
      value: trackingValue,
    })
  }

  const onSubmit = () => {
    const urlSegments = [
      `/${vehicleCondition}-cars`,
      ...(selectedMakeOption && selectedMakeOption !== 'any'
        ? [`${dashify(selectedMakeOption)}`]
        : []),
      ...(selectedModelOption && selectedModelOption !== 'any'
        ? [`${dashify(selectedModelOption)}`]
        : []),
    ]

    const urlParams = new URLSearchParams()
    if (selectedPriceFrequencyOption === PriceFrequency.MonthlyPrice) {
      urlParams.append('financeType', 'any')
    }

    const baseUrl = urlSegments.join('/')
    const redirectUrl =
      urlParams.toString().length > 0
        ? `${baseUrl}?${urlParams.toString()}`
        : baseUrl

    trackOnSubmit()
    window.location.assign(redirectUrl)
  }

  return (
    <form
      onSubmit={(event) => {
        event.preventDefault()
        onSubmit()
      }}
    >
      <fieldset className={styles['radioGroup']}>
        {PRICE_FREQUENCY_OPTIONS.map((option) => (
          <div key={option.value} className={styles['radioInputContainer']}>
            <label htmlFor={option.value} className={styles['priceLabel']}>
              <input
                type="radio"
                id={option.value}
                name={option.value}
                checked={option.value === selectedPriceFrequencyOption}
                onChange={() => onPriceFrequencyChange(option.value)}
              />
              {option.label}
            </label>
          </div>
        ))}
      </fieldset>
      <Price
        radioChecked={selectedPriceFrequencyOption}
        maxTotalPrice={selectedMaxTotalPrice}
        setMaxTotalPrice={setSelectedMaxTotalPrice}
        priceBucket={priceBucket}
        monthlyPriceBucket={monthlyPriceBucket}
        trackPriceChange={(value) => {
          if (selectedPriceFrequencyOption === PriceFrequency.MonthlyPrice) {
            trackDropdownOptionChange(
              'monthly price',
              'select',
              `${value}`,
              Actions.SELECT_MONTHLY_PRICE_OPTION,
            )
          } else {
            trackDropdownOptionChange(
              'total price',
              'select',
              `${value}`,
              Actions.SELECT_MAX_PRICE_OPTION,
            )
          }
        }}
      />
      <SelectField
        label="Select make"
        labelHidden
        id="make"
        name="make"
        data-testid="make-select"
        options={makeOptions}
        placeholder={'Select make'}
        placeholderValue={'any'}
        onChange={(event) => onMakeChange(event.target.value)}
        error={{
          show: false,
          classNames: { container: styles['errorBox'] },
        }}
      />
      <SelectField
        label="Select model"
        labelHidden
        id="model"
        name="model"
        data-testid="model-select"
        options={modelOptions}
        placeholder={'Select model'}
        placeholderValue={'any'}
        onChange={(event) => onModelChange(event.target.value)}
        disabled={!selectedMakeOption || selectedMakeOption === 'any'}
        error={{
          show: false,
          classNames: { container: styles['errorBox'] },
        }}
      />

      <div className={styles['buttonContainer']}>
        <Primary
          type="submit"
          classNames={{ button: styles['button'] }}
          data-testid="searchButton"
          isFullWidth
        >
          {searchResultsCount === 1
            ? 'Search 1 car'
            : `Search ${formatNumber(searchResultsCount)} cars`}
        </Primary>
      </div>
    </form>
  )
}
